import { invoke } from '@tauri-apps/api/core'
import { Webview } from '@tauri-apps/api/webview'
import { WebviewWindow } from '@tauri-apps/api/webviewWindow'
import { Window, currentMonitor, getCurrentWindow } from '@tauri-apps/api/window'
import objectHash from 'object-hash'
import { ITool, ToolInitStep } from 'types'
import { sleep } from 'utils'

export const labelHeight = 80

const registeredTools = new Set<string>()

export async function initTool(tool: ITool, width: number, height: number) {
  const monitor = await currentMonitor()
  if (!monitor) throw new Error('No monitor found')

  const key = makeKey(tool)
  if (registeredTools.has(key)) return key

  registeredTools.add(key)
  const { url, initSteps, title } = tool
  const { scaleFactor, size, position } = monitor

  const fullHeight = (size.height ?? 0) / scaleFactor
  const fullWidth = (size.width ?? 0) / scaleFactor
  const x = (position.x ?? 0) + (fullWidth - width) / 2
  const y = (position.y ?? 0) + (fullHeight - height) / 2

  const appWindow = new Window(key, {
    alwaysOnTop: true,
    closable: false,
    decorations: false,
    resizable: false,
    skipTaskbar: true,
    visible: false,
    x,
    y,
    height,
    width,
  })

  appWindow.once('tauri://created', async () => {
    new Webview(appWindow, titleLabel(key), {
      height: labelHeight,
      url: `/tool-header/?title=${title}`,
      width,
      x: 0,
      y: height - labelHeight,
    })

    const toolView = new Webview(appWindow, contentLabel(key), {
      acceptFirstMouse: true,
      height: height - labelHeight,
      url,
      width,
      x: 0,
      y: 0,
    })

    if (initSteps?.length) {
      toolView.once('tauri://created', async () => {
        await sleep(5000) // HACK - wait for url to finish loading?
        const js = insertJS(initSteps)

        while (true) {
          const { done, value } = await js.next()
          if (done) break

          await invoke('inject_js', { label: contentLabel(key), string: value })
        }
      })
    }
  })

  return key
}

let activeToolKey = ''

export const isToolActive = () => Boolean(activeToolKey)

export async function showTool(key?: string) {
  if (!key) return

  await hideActiveTool()
  const window = await getToolWindow(key)
  if (!window) throw 'Error showing. Tool not found'

  await window.show()
  activeToolKey = key
}

export function hideActiveTool() {
  return hideTool(activeToolKey)
}

export async function handleHeaderCloseButton() {
  activeToolKey = ''

  const currentWindow = await getCurrentWindow()
  if (currentWindow.label === 'main') return

  await currentWindow.hide()
}

export async function hideTool(key?: string) {
  if (!key) return

  const window = await getToolWindow(key)
  if (!window) throw 'Error hiding. Tool not found'

  await window.hide()
  activeToolKey = ''
}

export async function destroyTool(key?: string) {
  if (!key) return

  const window = await getToolWindow(key)
  registeredTools.delete(key)
  return window?.close()
}

function getToolWindow(key: string) {
  return WebviewWindow.getByLabel(key)
}

const makeKey = objectHash

const titleLabel = (key: string) => `toolTitle-${key}`
const contentLabel = (key: string) => `toolContent-${key}`

async function* insertJS(initSteps: ToolInitStep[]) {
  yield `
      let element;
      ${postGood}
      true;
    `

  function setProperties(property?: { [key: string]: string }) {
    if (!property) return

    return Object.entries(property).reduce((acc, [key, value]) => {
      return `${acc} element.${key} = ${value};`
    }, '')
  }

  function clickButton() {
    return `
        element.click()
        element.click() // some buttons need to be clicked twice?
      `
  }

  function fillInput(value: string = '') {
    return `element.value = '${value}'`
  }

  for (const step of initSteps) {
    await sleep(step.wait || 0)

    yield `
        element = document.querySelector('${step.selector}');
        if (element) {
          ${setProperties(step.property)}
          ${step.action === 'fill' ? fillInput(step.value) : clickButton()}
          ${postGood}
        } else {
          ${postBad}
        }
        true;
      `
  }
}

const GOOD = 'good'
const postGood = `window.ReactNativeWebView.postMessage('${GOOD}')`
const postBad = `window.ReactNativeWebView.postMessage('')`
